import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';

/**
 * The blocks an event page may contain, beyond text (D-88, PARTS 2, 6).
 *
 * Each one is a node with named attributes — an image is a library id, a
 * sponsor block is a sponsor id, a call to action is a label and a checked
 * URL. None of them holds markup, a style or a class, which is why the public
 * renderer can be a `switch` over known types rather than a sanitizer.
 *
 * What is rendered in the editor is a preview of the real thing, not the real
 * thing: the storefront's markup comes from the server-side renderer, so the
 * editor never becomes a second source of truth for how a page looks.
 */

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    contentBlocks: {
      insertLibraryImage: (attrs: { fileId: string; alt?: string; caption?: string | null; align?: string; size?: string }) => ReturnType;
      insertSponsorBlock: (attrs: { sponsorId: string; variant?: string; size?: string; label?: string | null }) => ReturnType;
      insertSponsorGroup: (attrs: { tier?: string | null; heading?: string | null; size?: string }) => ReturnType;
      insertCta: (attrs: { label: string; href: string; style?: string }) => ReturnType;
      toggleCallout: (tone: 'info' | 'warning' | 'success') => ReturnType;
    };
  }
}

export interface EditorSponsor {
  id: string;
  name: string;
  display_name: string | null;
  tier_code?: string | null;
  logos: { variant_code: string; file_id: string }[];
}

/** Everything the node views need that does not belong in the document. */
export interface ContentEditorContext {
  sponsors: EditorSponsor[];
}

let editorContext: ContentEditorContext = { sponsors: [] };
export const setEditorContext = (context: ContentEditorContext) => {
  editorContext = context;
};

const mediaUrl = (fileId: string) => `/api/public/media/${fileId}`;

// --- An image from the library ------------------------------------------------------------------

function ImageView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const attrs = node.attrs as { fileId: string; alt: string; caption: string | null; align: string; size: string };
  return (
    <NodeViewWrapper className={`content-node ${selected ? 'content-node-selected' : ''}`} data-align={attrs.align} data-size={attrs.size}>
      <img src={mediaUrl(attrs.fileId)} alt={attrs.alt} className="max-h-72 w-full rounded border border-line bg-page object-contain" />
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        <input
          value={attrs.alt ?? ''}
          onChange={(e) => updateAttributes({ alt: e.target.value })}
          placeholder="What the picture shows (read aloud to anyone who cannot see it)"
          aria-label="Alt text"
          className="min-w-64 flex-1 rounded border border-line bg-surface px-2 py-1"
        />
        <input
          value={attrs.caption ?? ''}
          onChange={(e) => updateAttributes({ caption: e.target.value })}
          placeholder="Caption (optional)"
          aria-label="Caption"
          className="w-48 rounded border border-line bg-surface px-2 py-1"
        />
        <select value={attrs.size} onChange={(e) => updateAttributes({ size: e.target.value })} aria-label="Size" className="rounded border border-line bg-surface px-2 py-1">
          {['small', 'medium', 'large', 'full'].map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        <select value={attrs.align} onChange={(e) => updateAttributes({ align: e.target.value })} aria-label="Alignment" className="rounded border border-line bg-surface px-2 py-1">
          {['left', 'center', 'right', 'full'].map((align) => (
            <option key={align} value={align}>
              {align}
            </option>
          ))}
        </select>
        <button type="button" onClick={deleteNode} className="rounded border border-bad/30 px-2 py-1 text-bad">
          Remove
        </button>
      </div>
    </NodeViewWrapper>
  );
}

export const LibraryImage = Node.create({
  name: 'image',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes: () => ({
    fileId: { default: null },
    alt: { default: '' },
    caption: { default: null },
    align: { default: 'center' },
    size: { default: 'medium' },
  }),
  parseHTML: () => [{ tag: 'div[data-content-image]' }],
  renderHTML: ({ HTMLAttributes }) => ['div', mergeAttributes(HTMLAttributes, { 'data-content-image': '' })],
  addNodeView: () => ReactNodeViewRenderer(ImageView),
  addCommands() {
    return {
      insertLibraryImage:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: 'image', attrs }),
    };
  },
});

// --- A sponsor -----------------------------------------------------------------------------------

function SponsorView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const attrs = node.attrs as { sponsorId: string; variant: string; size: string; label: string | null };
  const sponsor = editorContext.sponsors.find((s) => s.id === attrs.sponsorId) ?? null;
  const logo = sponsor?.logos.find((l) => l.variant_code === attrs.variant) ?? sponsor?.logos[0] ?? null;
  return (
    <NodeViewWrapper className={`content-node ${selected ? 'content-node-selected' : ''}`}>
      <div className={`rounded border border-line p-3 ${attrs.variant === 'REVERSED' || attrs.variant === 'WHITE' ? 'bg-shell-900' : 'bg-page'}`}>
        {attrs.label ? <p className="mb-1 text-xs tracking-wide text-ink-400 uppercase">{attrs.label}</p> : null}
        {sponsor ? (
          logo ? (
            <img src={mediaUrl(logo.file_id)} alt={sponsor.display_name ?? sponsor.name} className="max-h-20 object-contain" />
          ) : (
            <p className="text-sm font-medium">{sponsor.display_name ?? sponsor.name}</p>
          )
        ) : (
          <p className="text-sm text-bad">That sponsor is not attached to this event; the page will show nothing here.</p>
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        <input
          value={attrs.label ?? ''}
          onChange={(e) => updateAttributes({ label: e.target.value })}
          placeholder="Presented by"
          aria-label="Label above the logo"
          className="w-48 rounded border border-line bg-surface px-2 py-1"
        />
        <select value={attrs.sponsorId} onChange={(e) => updateAttributes({ sponsorId: e.target.value })} aria-label="Sponsor" className="rounded border border-line bg-surface px-2 py-1">
          {editorContext.sponsors.map((s) => (
            <option key={s.id} value={s.id}>
              {s.display_name ?? s.name}
            </option>
          ))}
        </select>
        <select value={attrs.variant} onChange={(e) => updateAttributes({ variant: e.target.value })} aria-label="Logo version" className="rounded border border-line bg-surface px-2 py-1">
          {['COLOR', 'REVERSED', 'BLACK', 'WHITE'].map((variant) => (
            <option key={variant} value={variant}>
              {variant.toLowerCase()}
            </option>
          ))}
        </select>
        <select value={attrs.size} onChange={(e) => updateAttributes({ size: e.target.value })} aria-label="Size" className="rounded border border-line bg-surface px-2 py-1">
          {['small', 'medium', 'large'].map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        <button type="button" onClick={deleteNode} className="rounded border border-bad/30 px-2 py-1 text-bad">
          Remove
        </button>
      </div>
    </NodeViewWrapper>
  );
}

export const SponsorBlock = Node.create({
  name: 'sponsorBlock',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes: () => ({ sponsorId: { default: null }, variant: { default: 'COLOR' }, size: { default: 'medium' }, label: { default: null } }),
  parseHTML: () => [{ tag: 'div[data-sponsor-block]' }],
  renderHTML: ({ HTMLAttributes }) => ['div', mergeAttributes(HTMLAttributes, { 'data-sponsor-block': '' })],
  addNodeView: () => ReactNodeViewRenderer(SponsorView),
  addCommands() {
    return {
      insertSponsorBlock:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: 'sponsorBlock', attrs }),
    };
  },
});

// --- Every sponsor at a level --------------------------------------------------------------------

function SponsorGroupView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const attrs = node.attrs as { tier: string | null; heading: string | null; size: string };
  const shown = editorContext.sponsors.filter((sponsor) => !attrs.tier || sponsor.tier_code === attrs.tier);
  return (
    <NodeViewWrapper className={`content-node ${selected ? 'content-node-selected' : ''}`}>
      <div className="rounded border border-line bg-page p-3">
        {attrs.heading ? <p className="mb-2 text-sm font-semibold">{attrs.heading}</p> : null}
        <div className="flex flex-wrap items-center gap-4">
          {shown.length === 0 ? <p className="text-sm text-ink-400">No sponsors at that level on this event yet.</p> : null}
          {shown.map((sponsor) => {
            const logo = sponsor.logos[0] ?? null;
            return logo ? (
              <img key={sponsor.id} src={mediaUrl(logo.file_id)} alt={sponsor.display_name ?? sponsor.name} className="max-h-12 object-contain" />
            ) : (
              <span key={sponsor.id} className="text-sm">
                {sponsor.display_name ?? sponsor.name}
              </span>
            );
          })}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        <input
          value={attrs.heading ?? ''}
          onChange={(e) => updateAttributes({ heading: e.target.value })}
          placeholder="With support from"
          aria-label="Heading"
          className="w-56 rounded border border-line bg-surface px-2 py-1"
        />
        <select value={attrs.tier ?? ''} onChange={(e) => updateAttributes({ tier: e.target.value || null })} aria-label="Level" className="rounded border border-line bg-surface px-2 py-1">
          <option value="">Every sponsor on this event</option>
          {['PRESENTING', 'MAJOR', 'SUPPORTING', 'MEDIA', 'IN_KIND', 'COMMUNITY'].map((tier) => (
            <option key={tier} value={tier}>
              {tier.replaceAll('_', ' ').toLowerCase()}
            </option>
          ))}
        </select>
        <button type="button" onClick={deleteNode} className="rounded border border-bad/30 px-2 py-1 text-bad">
          Remove
        </button>
      </div>
    </NodeViewWrapper>
  );
}

export const SponsorGroup = Node.create({
  name: 'sponsorGroup',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes: () => ({ tier: { default: null }, heading: { default: null }, size: { default: 'medium' } }),
  parseHTML: () => [{ tag: 'div[data-sponsor-group]' }],
  renderHTML: ({ HTMLAttributes }) => ['div', mergeAttributes(HTMLAttributes, { 'data-sponsor-group': '' })],
  addNodeView: () => ReactNodeViewRenderer(SponsorGroupView),
  addCommands() {
    return {
      insertSponsorGroup:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: 'sponsorGroup', attrs }),
    };
  },
});

// --- A call to action -----------------------------------------------------------------------------

function CtaView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const attrs = node.attrs as { label: string; href: string; style: string };
  return (
    <NodeViewWrapper className={`content-node ${selected ? 'content-node-selected' : ''}`}>
      <p>
        {/* Square, tracked, uppercase: what `.content-cta a` publishes (D-136). */}
        <span className={`inline-flex px-5 py-3 text-xs font-semibold tracking-widest uppercase ${attrs.style === 'secondary' ? 'border-2 border-brand text-brand' : 'bg-brand text-brand-contrast'}`}>{attrs.label || 'Button'}</span>
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        <input value={attrs.label} onChange={(e) => updateAttributes({ label: e.target.value })} placeholder="Buy tickets" aria-label="Button text" className="w-40 rounded border border-line bg-surface px-2 py-1" />
        <input
          value={attrs.href}
          onChange={(e) => updateAttributes({ href: e.target.value })}
          placeholder="/events/haunted-auditorium"
          aria-label="Where it goes"
          className="w-72 rounded border border-line bg-surface px-2 py-1"
        />
        <select value={attrs.style} onChange={(e) => updateAttributes({ style: e.target.value })} aria-label="Style" className="rounded border border-line bg-surface px-2 py-1">
          <option value="primary">Solid</option>
          <option value="secondary">Outline</option>
        </select>
        <button type="button" onClick={deleteNode} className="rounded border border-bad/30 px-2 py-1 text-bad">
          Remove
        </button>
      </div>
      {/^https?:/i.test(attrs.href) && !/^https:/i.test(attrs.href) ? <p className="mt-1 text-xs text-bad">Only https links are published.</p> : null}
    </NodeViewWrapper>
  );
}

export const CtaButton = Node.create({
  name: 'ctaButton',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes: () => ({ label: { default: 'Buy tickets' }, href: { default: '' }, style: { default: 'primary' } }),
  parseHTML: () => [{ tag: 'p[data-cta]' }],
  renderHTML: ({ HTMLAttributes }) => ['p', mergeAttributes(HTMLAttributes, { 'data-cta': '' })],
  addNodeView: () => ReactNodeViewRenderer(CtaView),
  addCommands() {
    return {
      insertCta:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: 'ctaButton', attrs }),
    };
  },
});

// --- A callout -------------------------------------------------------------------------------------

export const Callout = Node.create({
  name: 'callout',
  group: 'block',
  content: 'block+',
  defining: true,
  addAttributes: () => ({ tone: { default: 'info' } }),
  parseHTML: () => [{ tag: 'aside[data-callout]' }],
  renderHTML: ({ HTMLAttributes, node }) => [
    'aside',
    mergeAttributes(HTMLAttributes, { 'data-callout': '', class: `content-callout content-callout-${node.attrs['tone']}` }),
    0,
  ],
  addCommands() {
    return {
      toggleCallout:
        (tone) =>
        ({ commands }) =>
          commands.toggleWrap('callout', { tone }),
    };
  },
});

export const CONTENT_EXTENSIONS = [LibraryImage, SponsorBlock, SponsorGroup, CtaButton, Callout];
